'use client';

import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ToastContext, type CreateToastInput, type ToastContextValue, type ToastEntry } from './ToastContext';

/** Compact toasts on screen at once: a new one pushes out the oldest. */
const MAX_COMPACT_TOASTS = 3;

interface ToastProviderProps {
    children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
    const [queue, setQueue] = useState<ToastEntry[]>([]);

    const showToast = useCallback((input: CreateToastInput): string => {
        const id = crypto.randomUUID();
        setQueue((currentQueue) => {
            const queue = [...currentQueue, { id, ...input }];
            const compactIds = queue.filter((entry) => entry.compact).map((entry) => entry.id);
            const dropped = new Set(compactIds.slice(0, Math.max(0, compactIds.length - MAX_COMPACT_TOASTS)));
            return dropped.size > 0 ? queue.filter((entry) => !dropped.has(entry.id)) : queue;
        });
        return id;
    }, []);

    const dismissToast = useCallback((id: string): void => {
        setQueue((currentQueue) => currentQueue.filter((entry) => entry.id !== id));
    }, []);

    const contextValue = useMemo<ToastContextValue>(() => ({
        queue,
        showToast,
        dismissToast,
    }), [dismissToast, queue, showToast]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
        </ToastContext.Provider>
    );
}
